import React, { useState } from "react";
import { UseMutationResult } from "@tanstack/react-query";
import { HStack } from "@design-system/HStack/HStack";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { PlainModal, StandardModalHeader } from "./Modal";
import classNames from "classnames";
import styles from "./ConfirmationModal.module.scss";

/**
 * Button's own union keeps `destructive` apart from `primary | secondary`, so
 * the variant is split back into those groups before it reaches a Button.
 */
type ConfirmationVariant = "primary" | "secondary" | "destructive";

/**
 * A second way to say yes, for confirms that offer two outcomes rather than
 * yes/no. Rendered after the confirm button.
 */
export interface ConfirmationAlternative {
    label: string;
    variant?: ConfirmationVariant;
    disabled?: boolean;
    onConfirm: () => Promise<unknown> | void;
}

interface CommonProps {
    title?: React.ReactNode;
    question?: React.ReactNode;
    yes?: string;
    yesBtnVariant?: ConfirmationVariant;
    no?: string;
    noBtnVariant?: ConfirmationVariant;
    onReject?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    closeButton?: boolean;
    confirmDisabled?: boolean;
    confirmLoading?: boolean;
    alternative?: ConfirmationAlternative;

    onHide?: () => void;
    onExited?: () => void;
    show?: boolean;
    disableAnimation?: boolean;
    className?: string;
}

interface PromiseProps extends CommonProps {
    onConfirm: (e: any) => Promise<unknown> | void;
    onConfirmMutation?: never;
}

interface MutationProps extends CommonProps {
    onConfirm?: never;
    onConfirmMutation: UseMutationResult<unknown, unknown, void>;
}

export const ConfirmationModal: React.FC<PromiseProps | MutationProps> = ({
    title,
    question = "Are you sure?",
    yes = "Yes",
    no = "No",
    yesBtnVariant = "primary",
    noBtnVariant = "secondary",
    onConfirm,
    onConfirmMutation,
    onReject,
    confirmDisabled = false,
    confirmLoading = false,
    alternative,
    show,
    onHide,
    onExited,
    disableAnimation,
    className,
}) => {
    const [isConfirmLoading, setIsConfirmLoading] = useState(false);
    const loading = isConfirmLoading || confirmLoading;
    const [isAlternativeLoading, setIsAlternativeLoading] = useState(false);
    const isBusy =
        loading ||
        isAlternativeLoading ||
        (onConfirmMutation?.isPending ?? false);

    const rejectProps = {
        size: "xl",
        onClick: onReject,
        disabled: isBusy,
        "data-testid": "confirmation-modal-no-button",
        children: no,
        className: styles.noButton,
    } as const;

    const handleConfirm = async (
        event: React.MouseEvent<HTMLButtonElement>,
    ) => {
        if (onConfirmMutation) {
            onConfirmMutation.mutate();
            return;
        }
        await onConfirm?.(event);
    };

    const confirmProps = {
        size: "xl",
        disabled: confirmDisabled || isAlternativeLoading,
        "data-testid": "confirmation-modal-yes-button",
        children: yes,
        loading: onConfirmMutation ? onConfirmMutation.isPending : loading,
        setLoading: setIsConfirmLoading,
        onClick: handleConfirm,
    } as const;

    const alternativeProps = alternative
        ? ({
              size: "xl",
              disabled: (alternative.disabled ?? false) || loading,
              "data-testid": "confirmation-modal-alternative-button",
              children: alternative.label,
              loading: isAlternativeLoading,
              setLoading: setIsAlternativeLoading,
              onClick: async () => {
                  await alternative.onConfirm();
              },
          } as const)
        : null;

    return (
        <PlainModal
            onHide={onHide}
            onExited={onExited}
            disableAnimation={disableAnimation}
            show={show}
            className={classNames(
                className,
                alternative && styles.withAlternative,
            )}
        >
            <>
                {title && <StandardModalHeader>{title}</StandardModalHeader>}

                {question && (
                    <div
                        className="mb-4"
                        data-testid="confirmation-modal-question"
                    >
                        {question}
                    </div>
                )}
                <HStack as="footer" gap={8} justify="end">
                    {onReject &&
                        (noBtnVariant === "destructive" ? (
                            <Button variant="destructive" {...rejectProps} />
                        ) : (
                            <Button variant={noBtnVariant} {...rejectProps} />
                        ))}
                    {alternativeProps &&
                        (alternative?.variant === "destructive" ? (
                            <ButtonWithLoader
                                variant="destructive"
                                {...alternativeProps}
                            />
                        ) : (
                            <ButtonWithLoader
                                variant={alternative?.variant ?? "secondary"}
                                {...alternativeProps}
                            />
                        ))}
                    {yesBtnVariant === "destructive" ? (
                        <ButtonWithLoader
                            variant="destructive"
                            {...confirmProps}
                        />
                    ) : (
                        <ButtonWithLoader
                            variant={yesBtnVariant}
                            {...confirmProps}
                        />
                    )}
                </HStack>
            </>
        </PlainModal>
    );
};
